import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from '@mui/material';
import { PageHeader } from '../../components/ui';
import {
  Edit,
  Save,
  Search,
  Person,
  Close,
} from '@mui/icons-material';
import { teacherAPI, adminAPI, academicYearsAPI, termsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getExamTypeConfig, validateScore, examTypeToEnum } from '../../utils/dataMapping';

const ManualScoreEntry = () => {
  const { user, hasRole } = useAuth();
  const basePath = hasRole('Admin') ? '/admin-dashboard' : '/teacher-dashboard';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [classes, setClasses] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [teacherAssignments, setTeacherAssignments] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [currentAcademicYear, setCurrentAcademicYear] = useState(null);
  const [currentTerm, setCurrentTerm] = useState(null);

  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');

  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [examType, setExamType] = useState('ObjectiveOnly');
  const [scoreForm, setScoreForm] = useState({
    objectiveScore: '',
    theoryScore: '',
    testScore: '',
    remarks: '',
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedAcademicYear) {
      loadTermsByAcademicYear(selectedAcademicYear);
    }
  }, [selectedAcademicYear]);

  useEffect(() => {
    if (selectedClass && selectedClass !== '') {
      loadClassSubjects(selectedClass);
      loadStudents(selectedClass);
      setSelectedSubject('');
    } else {
      setClassSubjects([]);
      setStudents([]);
      setFilteredStudents([]);
      setSelectedSubject('');
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedClass && selectedSubject && selectedAcademicYear && selectedTerm) {
      loadExistingScores();
    }
  }, [selectedClass, selectedSubject, selectedAcademicYear, selectedTerm]);

  useEffect(() => {
    // Filter students based on search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      setFilteredStudents(
        students.filter(
          (s) =>
            s.firstName?.toLowerCase().includes(query) ||
            s.lastName?.toLowerCase().includes(query) ||
            s.studentNumber?.toLowerCase().includes(query)
        )
      );
    } else {
      setFilteredStudents(students);
    }
  }, [searchQuery, students]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      const isTeacher = hasRole('Teacher');
      
      if (isTeacher) {
        // Teachers can only see classes they're assigned to
        const assignmentsRes = await teacherAPI.classSubjects.getMyAssignments();
        if (assignmentsRes.data?.success) {
          const assignments = assignmentsRes.data.data?.items || [];
          setTeacherAssignments(assignments);
          const uniqueClasses = [];
          const seenClassIds = new Set();
          assignments.forEach(a => {
            const classId = a.classId || (a.class && a.class.id);
            if (a.class && !seenClassIds.has(classId)) {
              seenClassIds.add(classId);
              uniqueClasses.push(a.class);
            }
          });
          setClasses(uniqueClasses);
        }
      } else {
        const classesRes = await adminAPI.classes.getAll();
        if (classesRes.data?.success) setClasses(classesRes.data.data);
      }
      
      const [academicYearsRes] = await Promise.all([
        academicYearsAPI.getAll(),
      ]);

      if (academicYearsRes.data?.success) {
        setAcademicYears(academicYearsRes.data.data);
        const current = academicYearsRes.data.data.find((y) => y.isActive);
        if (current) {
          setSelectedAcademicYear(current.id);
          setCurrentAcademicYear(current);
        }
      }
    } catch (err) {
      setError('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  const loadTermsByAcademicYear = async (academicYearId) => {
    try {
      const termsRes = await termsAPI.getByAcademicYear(academicYearId);
      if (termsRes.data?.success) {
        setTerms(termsRes.data.data);
        const current = termsRes.data.data.find((t) => t.isActive);
        if (current) {
          setSelectedTerm(current.id);
          setCurrentTerm(current);
        } else if (termsRes.data.data.length > 0) {
          setSelectedTerm(termsRes.data.data[0].id);
          setCurrentTerm(termsRes.data.data[0]);
        }
      }
    } catch (err) {
      console.error('Error loading terms:', err);
    }
  };

  const loadClassSubjects = async (classId) => {
    try {
      setError('');
      const isTeacher = hasRole('Teacher');
      
      console.log('loadClassSubjects: classId=', classId, 'isTeacher=', isTeacher, 'teacherAssignments=', teacherAssignments.length);
      
      if (isTeacher && teacherAssignments.length > 0) {
        const classAssignments = teacherAssignments.filter(
          a => String(a.classId) === String(classId)
        );
        
        const uniqueSubjects = [];
        classAssignments.forEach(a => {
          const subjectData = {
            id: a.id,
            name: a.subject?.name || a.subject?.Name || `Subject (ID: ${a.subjectId?.substring(0,8)})`,
            subject: a.subject,
          };
          if (!uniqueSubjects.find(s => s.id === a.id)) {
            uniqueSubjects.push(subjectData);
          }
        });
        setClassSubjects(uniqueSubjects);
      } else {
        const response = await (hasRole('Admin') ? adminAPI : teacherAPI).classSubjects.getByClass(classId);
        console.log('loadClassSubjects admin - response:', response.data);
        if (response.data?.success && Array.isArray(response.data.data)) {
          const subjects = response.data.data.map(cs => ({
            id: cs.id,
            name: cs.name || cs.Subject?.Name || cs.subject?.name || '',
            subject: cs.subject || cs.Subject,
          }));
          setClassSubjects(subjects);
        } else if (response.data?.data?.items && Array.isArray(response.data.data.items)) {
          const subjects = response.data.data.items.map(cs => ({
            id: cs.id,
            name: cs.name || cs.Subject?.Name || cs.subject?.name || '',
            subject: cs.subject || cs.Subject,
          }));
          setClassSubjects(subjects);
        } else {
          setClassSubjects([]);
        }
      }
    } catch (err) {
      console.error('Failed to load subjects:', err);
      setError('Failed to load subjects');
      setClassSubjects([]);
    }
  };

  const loadStudents = async (classId) => {
    try {
      const apiToUse = hasRole('Admin') ? adminAPI : teacherAPI;
      const response = await apiToUse.students.getByClass(classId);
      console.log('Students response:', response.data);
      if (response.data?.success && Array.isArray(response.data.data)) {
        const studentsList = response.data.data;
        setStudents(studentsList);
        setFilteredStudents(studentsList);
      } else if (response.data?.data?.items && Array.isArray(response.data.data.items)) {
        setStudents(response.data.data.items);
        setFilteredStudents(response.data.data.items);
      } else {
        setStudents([]);
        setFilteredStudents([]);
      }
    } catch (err) {
      console.error('Failed to load students:', err);
      setError('Failed to load students');
      setStudents([]);
      setFilteredStudents([]);
    }
  };

  const loadExistingScores = async () => {
    try {
      if (!selectedAcademicYear || !selectedTerm || students.length === 0) {
        console.log('loadExistingScores skipped - missing params or no students');
        return;
      }

      const yearId = selectedAcademicYear;
      const termInfo = terms.find((t) => t.id === selectedTerm);
      const subjectInfo = classSubjects.find((cs) => cs.id === selectedSubject);
      
      const subjectName = subjectInfo?.name || '';
      
      console.log('=== loadExistingScores ===');
      console.log('subjectInfo:', subjectInfo);
      console.log('subjectName resolved:', subjectName);
      console.log('termInfo.name:', termInfo?.name);
      
      const updatedStudents = await Promise.all(
        students.map(async (student) => {
          try {
            const response = await adminAPI.scores.getStudentScores(student.id, yearId);
            
            if (response.data?.success) {
              const scores = response.data.data || [];
              console.log('Scores for', student.firstName, 'length:', scores.length);
              
              const existingScore = scores.find(
                (s) => {
                  const match = s.subjectName === subjectName && s.termName === termInfo?.name;
                  if (!match && s.subjectName && s.termName) {
                    console.log('  Mismatch:', s.subjectName, 'vs', subjectName, '|', s.termName, 'vs', termInfo?.name);
                  }
                  return match;
                }
              );
              return { ...student, existingScore };
            }
          } catch (e) {
            console.error('Error fetching score for student', student.id, e);
          }
          return student;
        })
      );
      console.log('Updated students with scores:', updatedStudents.filter(s => s.existingScore).length);
      setStudents(updatedStudents);
      setFilteredStudents(updatedStudents);
    } catch (err) {
      console.error('Error loading scores:', err);
    }
  };

  const handleOpenScoreDialog = (student) => {
    setSelectedStudent(student);
    setExamType('ObjectiveOnly');
    setScoreForm({
      objectiveScore: student.existingScore?.objectiveScore?.toString() || '',
      theoryScore: student.existingScore?.theoryScore?.toString() || '',
      testScore: student.existingScore?.testScore?.toString() || '',
      remarks: student.existingScore?.teacherRemarks || '',
    });
    setScoreDialogOpen(true);
    setError('');
    setSuccess('');
  };

  const handleCloseScoreDialog = () => {
    setScoreDialogOpen(false);
    setSelectedStudent(null);
    setExamType('ObjectiveOnly');
    setScoreForm({
      objectiveScore: '',
      theoryScore: '',
      testScore: '',
      remarks: '',
    });
  };

  const handleScoreChange = (e) => {
    const { name, value } = e.target;
    setScoreForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateAndSaveScore = async () => {
    // Use the selected examType instead of auto-determining
    const validation = validateScore(
      examType,
      scoreForm.objectiveScore ? parseInt(scoreForm.objectiveScore) : null,
      scoreForm.theoryScore ? parseInt(scoreForm.theoryScore) : null,
      scoreForm.testScore ? parseInt(scoreForm.testScore) : null
    );

    if (!validation.isValid) {
      setError(validation.errors.join(', '));
      return;
    }

    setSaving(true);
    setError('');

    try {
      const academicYearId = selectedAcademicYear;
      const termId = selectedTerm;
      
      if (!academicYearId || academicYearId === '' || !termId || termId === '') {
        setError('Please select academic year and term');
        return;
      }

      console.log('Saving score with:', {
        studentId: selectedStudent.id,
        classId: selectedClass,
        subjectId: selectedSubject,
        academicYearId: academicYearId,
        termId: termId,
      });

      const dto = {
        studentId: selectedStudent.id,
        classId: selectedClass,
        subjectId: selectedSubject,
        academicYearId: academicYearId,
        termId: termId,
        examType: examTypeToEnum(examType),
        objectiveScore: scoreForm.objectiveScore ? parseInt(scoreForm.objectiveScore) : null,
        theoryScore: scoreForm.theoryScore ? parseInt(scoreForm.theoryScore) : null,
        testScore: scoreForm.testScore ? parseInt(scoreForm.testScore) : null,
        remarks: scoreForm.remarks,
      };

      // Use adminAPI since teacherAPI might have caching issues
      const response = await adminAPI.scores.manual(dto);
      console.log('API Response:', response);

      if (response.data?.success) {
        setSuccess('Score saved successfully!');
        setTimeout(() => {
          handleCloseScoreDialog();
          loadExistingScores();
        }, 1500);
      } else {
        setError(response.data?.message || 'Failed to save score');
      }
    } catch (err) {
      console.error('Save score error:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.message || err.response?.data || 'Failed to save score');
    } finally {
      setSaving(false);
    }
  };

  const determineExamType = () => {
    return examType;
  };

  const getExamTypeDisplay = () => {
    const config = getExamTypeConfig(examType);
    return config.scoringDescription;
  };

  const calculateTotal = () => {
    const obj = parseInt(scoreForm.objectiveScore) || 0;
    const th = parseInt(scoreForm.theoryScore) || 0;
    const tst = parseInt(scoreForm.testScore) || 0;
    return obj + th + tst;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#FF3E8A' }} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Manual Score Entry"
        subtitle="Manually enter student scores without requiring them to take exams"
      />

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                select
                label="Class"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
              >
                <MenuItem value="">Select Class</MenuItem>
                {classes.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                select
                label="Subject"
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                disabled={!selectedClass}
              >
                <MenuItem value="">Select Subject</MenuItem>
                {classSubjects.map((cs) => (
                  <MenuItem key={cs.id} value={cs.id}>
                    {cs.name || `Subject (ID: ${cs.id?.substring(0,8)})`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                select
                label="Academic Year"
                value={selectedAcademicYear}
                onChange={(e) => setSelectedAcademicYear(e.target.value)}
              >
                <MenuItem value="">Select Year</MenuItem>
                {academicYears.map((ay) => (
                  <MenuItem key={ay.id} value={ay.id}>
                    {ay.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                select
                label="Term"
                value={selectedTerm}
                onChange={(e) => setSelectedTerm(e.target.value)}
              >
                <MenuItem value="">Select Term</MenuItem>
                {terms.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {selectedClass && selectedSubject && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Students ({filteredStudents.length})
              </Typography>
              <TextField
                size="small"
                placeholder="Search students..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 250 }}
              />
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Matric No</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Student Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Objective</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Theory</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Test</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStudents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3, color: '#666' }}>
                        No students found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStudents.map((student) => (
                      <TableRow key={student.id} hover>
                        <TableCell>{student.studentNumber || '-'}</TableCell>
                        <TableCell>
                          {student.firstName} {student.lastName}
                        </TableCell>
                        <TableCell>{student.existingScore?.objectiveScore || '-'}</TableCell>
                        <TableCell>{student.existingScore?.theoryScore || '-'}</TableCell>
                        <TableCell>{student.existingScore?.testScore || '-'}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#FF3E8A' }}>
                          {student.existingScore?.totalScore || '-'}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenScoreDialog(student)}
                            sx={{
                              backgroundColor: '#FF3E8A',
                              color: '#fff',
                              '&:hover': { backgroundColor: '#e6337a' },
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Score Entry Dialog */}
      <Dialog open={scoreDialogOpen} onClose={handleCloseScoreDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Enter Score - {selectedStudent?.firstName} {selectedStudent?.lastName}
          <IconButton onClick={handleCloseScoreDialog}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Exam Type"
            value={examType}
            onChange={(e) => setExamType(e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="ObjectiveOnly">Objective Only</MenuItem>
            <MenuItem value="ObjectiveAndTheory">Objective + Theory</MenuItem>
            <MenuItem value="ObjectiveAndTest">Objective + Test</MenuItem>
            <MenuItem value="ObjectiveTheoryAndTest">Objective + Theory + Test</MenuItem>
          </TextField>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Objective - always shown */}
            <Grid item xs={12} sm={examType === 'ObjectiveOnly' ? 12 : 4}>
              <TextField
                fullWidth
                label="Objective Score"
                name="objectiveScore"
                type="number"
                value={scoreForm.objectiveScore}
                onChange={handleScoreChange}
                required
                InputProps={{ inputProps: { min: 0, max: 100 } }}
              />
            </Grid>

            {/* Theory - shown for ObjectiveAndTheory and ObjectiveTheoryAndTest */}
            {examType === 'ObjectiveAndTheory' || examType === 'ObjectiveTheoryAndTest' ? (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Theory Score"
                  name="theoryScore"
                  type="number"
                  value={scoreForm.theoryScore}
                  onChange={handleScoreChange}
                  required
                  InputProps={{ inputProps: { min: 0, max: 100 } }}
                />
              </Grid>
            ) : null}

            {/* Test - shown for ObjectiveAndTest and ObjectiveTheoryAndTest */}
            {examType === 'ObjectiveAndTest' || examType === 'ObjectiveTheoryAndTest' ? (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Test Score"
                  name="testScore"
                  type="number"
                  value={scoreForm.testScore}
                  onChange={handleScoreChange}
                  required
                  InputProps={{ inputProps: { min: 0, max: 100 } }}
                />
              </Grid>
            ) : null}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Total"
                value={calculateTotal()}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ fontWeight: 'bold' }}>/100</Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                name="remarks"
                multiline
                rows={2}
                value={scoreForm.remarks}
                onChange={handleScoreChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseScoreDialog}>Cancel</Button>
          <Button
            onClick={validateAndSaveScore}
            variant="contained"
            disabled={saving}
            startIcon={<Save />}
            sx={{ backgroundColor: '#FF3E8A', '&:hover': { backgroundColor: '#e6337a' } }}
          >
            {saving ? 'Saving...' : 'Save Score'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManualScoreEntry;